/**
 * Origin gate for the demo login page (same-origin demo auth). Shared by the route guard and the
 * page itself, which each used to carry their own copy of the list, so widening one silently left
 * the other redirecting.
 *
 * Matched against the parsed host rather than as a string prefix. A prefix test on
 * `http://localhost` also admits `http://localhost.evil.com` and `http://localhost@evil.com`, and
 * one on `https://demo` admits `https://demo-anything.example.com`.
 */
// Each with its admin. and modules. hosts (config/local-hosts.txt); the admin portal runs its own
// demo login (src/admin/pages/AdminLoginDemoPage).
const LOCAL_DEV_HOSTNAMES = ['localhost', 'admin.localhost', 'modules.localhost'];
const SECURE_ALLOWED_HOSTNAMES = ['local.kuder.io', 'admin.local.kuder.io', 'modules.local.kuder.io'];

/*
 * The demo environment's real hostname is not recorded anywhere in this repo, so its entry stays a
 * leading-label rule instead of a guess that could lock the demo env out of its own login page.
 * Requiring the dot is what separates `demo.kuder.io` from `demo-anything.example.com`.
 */
const DEMO_HOSTNAME_LABEL = 'demo.';

export const isDemoLoginAllowedOrigin = (origin: string): boolean => {
    let url: URL;
    try {
        url = new URL(origin);
    }
    catch {
        return false;
    }

    // Any port, since local dev picks its own. Plaintext is confined to localhost so the demo
    // secret is never posted in the clear to a real host.
    if (url.protocol === 'http:') {
        return LOCAL_DEV_HOSTNAMES.includes(url.hostname);
    }

    if (url.protocol !== 'https:') {
        return false;
    }

    return SECURE_ALLOWED_HOSTNAMES.includes(url.hostname)
        || url.hostname.startsWith(DEMO_HOSTNAME_LABEL);
};
